import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  TravelVoucherFormRule,
  TravelVoucherFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultTravelVoucher } from "@factory/form/form-default-travel-voucher.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseTravelVoucherModel } from "@factory/model/query-model-travel-voucher";
import {
  useQyDeleteTravelVoucher,
  useQyGetTravelVoucherById,
  useQyUpdateTravelVoucher,
} from "@factory/query/travel-voucher.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import html2pdf from "html2pdf.js";
import { confirmDialog } from "primereact/confirmdialog";
import { useRef } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditTravelVoucher() {
  // LOCAL DECLARATION
  const fileRef = useRef<HTMLDivElement>(null);
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTravelVoucher();
  const navigate = useNavigate();
  const { mapUpdateTravelVoucher, mapDeleteTravelVoucher } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { travelVoucherId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Download", type: "split", value: ActionEnum.Download },
    { label: "Update", type: "more", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID TravelVoucher
  const handleGetApiSuccess = (data: GetResponseTravelVoucherModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("baseInvoice", responseData?.base_invoice || "");
    setValue("confirmationNum", responseData?.confirmation_num || "");
    setValue("paymentCompletion", responseData?.payment_completion);
    setValue("isFullyPaid", Boolean(responseData?.is_fully_paid));
    setValue("amountPaid", responseData?.amount_paid);
    setValue("amount", responseData?.amount);
  };
  const {
    data: travelVoucherResponse,
    isLoading,
    isError: travelVoucherError,
  } = useQyGetTravelVoucherById(travelVoucherId || "", handleGetApiSuccess);
  const travelVoucherData = travelVoucherResponse?.data?.[0];

  // API UPDATE TravelVoucher
  const handleUpdateApiSuccess = () => {
    showSuccess("TravelVoucher updated successfully");
  };
  const { mutate: updateTravelVoucher, isLoading: isEditLoading } =
    useQyUpdateTravelVoucher(handleUpdateApiSuccess);

  // API DELETE TravelVoucher
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("TravelVoucher deleted successfully");
  };
  const { mutate: deleteTravelVoucher } = useQyDeleteTravelVoucher(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<TravelVoucherFormSchema>({
    defaultValues: getDefault(travelVoucherData),
    resolver: zodResolver(TravelVoucherFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TravelVoucherFormSchema) => {
    if (!travelVoucherData || !travelVoucherData.code) {
      throw new Error("No travelVoucher data");
    }

    const formData = mapUpdateTravelVoucher(form, travelVoucherData.code);
    updateTravelVoucher(formData);
  };
  const handleValidateError = (err: FieldErrors<TravelVoucherFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!travelVoucherData || !travelVoucherData.code) {
      throw new Error("No travelVoucher data found");
    }

    const form = mapDeleteTravelVoucher(travelVoucherData.code);
    deleteTravelVoucher(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Download:
        downloadPdf(
          `Travel_Voucher-${travelVoucherData?.confirmation_num}.pdf`
        );
        break;
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const downloadPdf = (fileName: string) => {
    if (!fileRef.current) return;

    const opt = {
      margin: 0.1,
      filename: fileName,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2, // Increase scale for better quality
        useCORS: true, // Enable CORS
        allowTaint: true, // Allow cross-origin images
        logging: true, // Enable logging for debugging
        imageTimeout: 15000, // Increase timeout
      },
      jsPDF: {
        orientation: "portrait",
        unit: "pt", // use points
        format: "legal",
        // format: [pdfWidth, pdfHeight],
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().set(opt).from(fileRef.current).save();
  };

  return {
    travelVoucherId,
    travelVoucherData,
    isLoading,
    travelVoucherError,
    formMethod,
    actions,
    isEditLoading,
    fileRef,
    downloadPdf,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
